import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Rocket as RocketIcon } from '@mui/icons-material';

export default function DeployDialog({ open, tenant, onClose, onDeploy, currentEnvironment }) {
  const [environment, setEnvironment] = useState('test');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setEnvironment('test');
      setLoading(false);
      setError('');
    }
  }, [open]);

  const handleDeploy = async () => {
    setLoading(true);
    setError('');

    try {
      await onDeploy(environment);
    } catch (err) {
      // Extract the error message from axios response
      const errorMessage = err.response?.data?.error || err.message || 'Failed to deploy tenant';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketIcon color="primary" />
          Deploy Tenant to Another Environment
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {tenant && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This will copy the tenant configuration from <strong>{currentEnvironment?.toUpperCase()}</strong> to the selected environment:
            </Typography>
            <Typography variant="h6" fontFamily="monospace">
              {tenant.name} ({tenant.tenant})
            </Typography>
          </Box>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          The tenant will be deployed (copied) to the target environment. This creates a new instance of the tenant in that environment.
        </Alert>

        <FormControl component="fieldset">
          <FormLabel component="legend">Select Target Environment</FormLabel>
          <RadioGroup
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
          >
            <FormControlLabel
              value="test"
              control={<Radio />}
              disabled={currentEnvironment === 'test'}
              label={
                <Box>
                  <Typography variant="body1">Test</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentEnvironment === 'test' ? 'Current environment' : 'Development environment for testing'}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="qa"
              control={<Radio />}
              disabled={currentEnvironment === 'qa'}
              label={
                <Box>
                  <Typography variant="body1">QA</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentEnvironment === 'qa' ? 'Current environment' : 'Quality assurance environment'}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="prod"
              control={<Radio />}
              disabled={currentEnvironment === 'prod'}
              label={
                <Box>
                  <Typography variant="body1">Production</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentEnvironment === 'prod' ? 'Current environment' : 'Live production environment'}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleDeploy}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <RocketIcon />}
        >
          Deploy
        </Button>
      </DialogActions>
    </Dialog>
  );
}
